/**
 * Inventario.
 *
 * El stock **no es un número editable**: se deriva sumando movimientos. Un
 * número que se puede escribir a mano es un número que termina mintiendo, y por
 * eso acá no hay ningún campo «existencias».
 */

import { useState } from 'react';
import { parseMoneyInput, today } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import {
  Async, Badge, Button, Card, Cell, Empty, Field, Modal, Money, Note, PageHead, Table, TableSkeleton,
} from '@/components/ui';
import { PhotoUploader } from '@/components/common/PhotoUploader';
import { ProductThumb } from '@/components/common/ProductThumb';
import {
  isSupplierChosen, NO_SUPPLIER, resolveSupplierId, SupplierPicker, supplierChoiceOf,
  useSuppliers, type SupplierChoice,
} from '@/components/common/SupplierPicker';
import { SupplierFormModal } from './Suppliers';

const CONSUMPTION_FREQUENCY_LABEL: Record<string, string> = {
  DAILY: 'Diaria', WEEKLY: 'Semanal', MONTHLY: 'Mensual',
};

interface Item {
  id: string; name: string; sku: string | null; category: string | null;
  consumption_unit: string; purchase_unit: string; purchase_unit_quantity: number;
  stock: number; minimum_stock: number; below_minimum: boolean;
  latest_price_minor: number | null; supplier_name: string | null; last_movement: string | null;
  preferred_supplier_id: string | null;
  hazardous: number;
  hazard_composition: string | null; hazard_usage: string | null; hazard_dilution: string | null;
  hazard_safety_measures: string | null; hazard_supplier_info: string | null;
  consumption_quantity: number | null; consumption_frequency: string | null;
  photo_key: string | null;
}

interface Consumption {
  by_item: { name: string; consumption_unit: string; quantity: number; cost_minor: number }[];
  by_unit: { unit_code: string; name: string | null; cost_minor: number }[];
  by_month: { month: string; cost_minor: number }[];
}

export function InventoryPage() {
  const items = useApi<{ items: Item[] }>('/admin/inventory');
  const consumption = useApi<Consumption>('/admin/inventory/consumption');
  const suppliers = useSuppliers();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [consuming, setConsuming] = useState<Item | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  /* El regreso del alta rápida: el proveedor que se creó con solo el nombre al
     guardar un producto o una compra. Vive acá y no dentro del modal que lo
     originó porque ese modal ya se cerró — es justamente lo que pidió el
     dueño: «inmediatamente después de que uno cierra el editar del producto y
     guardó, ese proveedor tiene que ir a rellenar el resto de su información».

     Se guarda el id y la ficha se busca en la lista ya refrescada: la mutación
     invalida `/admin/suppliers` antes de avisar que terminó, así que para
     cuando esto se pinta el proveedor nuevo ya está en la caché. */
  const [completing, setCompleting] = useState<string | null>(null);
  const supplierToComplete = completing
    ? suppliers.data?.suppliers.find((s) => s.id === completing) ?? null
    : null;

  return (
    <>
      <PageHead
        title="Inventario"
        subtitle="Se compra por caja y se consume por unidad. El stock sale de los movimientos, no de un campo."
        actions={
          <>
            <Button onClick={() => setPurchasing(true)}>Nueva compra</Button>
            <Button variant="primary" onClick={() => setCreating(true)}>Nuevo producto</Button>
          </>
        }
      />

      <Async
        query={items}
        empty={(d) => d.items.length === 0 && (
          <Empty icon="inventario" title="Todavía no hay productos" hint="Cargá el catálogo de insumos." />
        )}
        skeleton={<TableSkeleton rows={6} cols={9} />}
      >
        {(data) => {
          const low = data.items.filter((i) => i.below_minimum);
          return (
            <>
              {low.length > 0 && (
                <Note tone="warn">
                  <strong>{low.length}</strong> producto{low.length === 1 ? '' : 's'} bajo el mínimo:
                  {' '}{low.map((i) => i.name).join(', ')}.
                </Note>
              )}

              <Card flush>
                <Table head={
                  /* Producto (identifica la fila) y Stock (el estado: si está
                     bajo el mínimo, sale el badge rojo) son lo que se mira
                     primero acá — es la pregunta que responde esta pantalla
                     («¿qué me está faltando?»). La miniatura, el proveedor, el
                     mínimo y el costo son detalle: se ven al abrir Editar. Los
                     tres botones de acción quedan visibles en el teléfono —
                     ocultarlos sacaría la única forma de usarlos ahí. */
                  <tr>
                    <th className="col-hide-sm" /><th>Producto</th>
                    <th className="col-hide-sm">Proveedor</th><th className="right">Stock</th>
                    <th className="right col-hide-sm">Mínimo</th>
                    <th className="right col-hide-sm">Costo unitario</th><th /><th /><th />
                  </tr>
                }>
                  {data.items.map((i) => (
                    <tr key={i.id}>
                      <td className="col-hide-sm">
                        <ProductThumb
                          photoSrc={i.photo_key ? `/api/admin/inventory/items/${i.id}/photo?v=${i.photo_key}` : null}
                        />
                      </td>
                      <td className="strong">
                        <Cell sub={[
                          i.category ?? i.sku,
                          i.consumption_quantity && i.consumption_frequency
                            ? `~${i.consumption_quantity} ${i.consumption_unit}/${CONSUMPTION_FREQUENCY_LABEL[i.consumption_frequency]?.toLowerCase()}`
                            : null,
                        ].filter(Boolean).join(' · ') || undefined}>
                          {i.name}
                          {Boolean(i.hazardous) && (
                            <Badge tone="warn" title="Producto peligroso: tiene hoja de seguridad">
                              {' '}⚠ peligroso
                            </Badge>
                          )}
                        </Cell>
                      </td>
                      <td className="small muted col-hide-sm">{i.supplier_name ?? '—'}</td>
                      <td className="right">
                        {i.below_minimum
                          ? <Badge tone="danger">{i.stock} {i.consumption_unit}</Badge>
                          : <>{i.stock} <span className="muted small">{i.consumption_unit}</span></>}
                      </td>
                      <td className="right muted small col-hide-sm">{i.minimum_stock}</td>
                      <td className="right col-hide-sm"><Money value={i.latest_price_minor} dim /></td>
                      <td className="right"><Button onClick={() => setEditing(i)}>Editar</Button></td>
                      <td className="right">
                        <Button onClick={() => setConsuming(i)}>Registrar consumo</Button>
                      </td>
                      <td className="right">
                        {i.consumption_quantity && i.consumption_frequency && (
                          <GenerateConsumptionButton item={i} />
                        )}
                      </td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </>
          );
        }}
      </Async>

      <div className="grid grid--2 mt-3">
        <Async query={consumption} skeleton={<TableSkeleton rows={4} cols={3} />}>
          {(data) => (
            <Card title="Consumo por producto" flush>
              {data.by_item.length === 0 ? (
                <Empty icon="inventario" title="Sin consumo registrado" />
              ) : (
                <Table head={<tr><th>Producto</th><th className="right">Cantidad</th><th className="right">Costo</th></tr>}>
                  {data.by_item.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td className="right">{r.quantity} <span className="muted small">{r.consumption_unit}</span></td>
                      <td className="right"><Money value={r.cost_minor} /></td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          )}
        </Async>

        <Async query={consumption} skeleton={<TableSkeleton rows={4} cols={2} />}>
          {(data) => (
            <Card title="Consumo por consultorio" flush>
              {data.by_unit.length === 0 ? (
                <Empty icon="inventario" title="Sin consumo asignado a un consultorio" />
              ) : (
                <Table head={<tr><th>Consultorio</th><th className="right">Costo</th></tr>}>
                  {data.by_unit.map((r) => (
                    <tr key={r.unit_code}>
                      <td><Cell sub={r.name}>{r.unit_code}</Cell></td>
                      <td className="right"><Money value={r.cost_minor} /></td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>
          )}
        </Async>
      </div>

      {creating && (
        <ItemModal item={null} onClose={() => setCreating(false)} onSupplierCreated={setCompleting} />
      )}
      {editing && (
        <ItemModal item={editing} onClose={() => setEditing(null)} onSupplierCreated={setCompleting} />
      )}
      {consuming && <ConsumptionModal item={consuming} onClose={() => setConsuming(null)} />}
      {purchasing && (
        <NewPurchaseModal onClose={() => setPurchasing(false)} onSupplierCreated={setCompleting} />
      )}

      {supplierToComplete && (
        <SupplierFormModal
          supplier={supplierToComplete}
          onClose={() => setCompleting(null)}
          intro={
            <Note tone="ok">
              Se creó <strong>{supplierToComplete.name}</strong>. Completale los datos ahora o
              dejalo para después: ya quedó guardado y disponible.
            </Note>
          }
        />
      )}
    </>
  );
}

/**
 * Convierte el consumo esperado del producto en un movimiento de salida real
 * del mes en curso. Un clic — no hay nada que llenar — y el servidor evita
 * duplicar la salida si ya se generó este mes (§4).
 */
function GenerateConsumptionButton({ item }: { item: Item }) {
  const mutation = useApiMutation(
    () => api.post(`/admin/inventory/items/${item.id}/generate-consumption`, {}),
    ['/admin/inventory'],
  );

  return (
    <div>
      <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? 'Generando…' : 'Generar consumo del mes'}
      </Button>
      {mutation.error && (
        <div className="small mt-1" style={{ color: 'var(--danger)' }}>{mutation.error.message}</div>
      )}
    </div>
  );
}

/**
 * Nuevo producto o edición. La información permanente del producto —lo que
 * es— se separa de las compras y el consumo, que son movimientos aparte
 * (§14): acá no hay ningún campo de cantidad comprada.
 */
function ItemModal({ item, onClose, onSupplierCreated }: {
  item: Item | null;
  onClose: () => void;
  onSupplierCreated: (id: string) => void;
}) {
  const suppliers = useSuppliers();
  const [supplier, setSupplier] = useState<SupplierChoice>(
    supplierChoiceOf(item?.preferred_supplier_id));
  const [form, setForm] = useState({
    name: item?.name ?? '', sku: item?.sku ?? '', category: item?.category ?? '',
    purchase_unit: item?.purchase_unit ?? 'caja',
    purchase_unit_quantity: String(item?.purchase_unit_quantity ?? '1'),
    consumption_unit: item?.consumption_unit ?? 'unidad',
    minimum_stock: String(item?.minimum_stock ?? '0'),
    consumption_quantity: item?.consumption_quantity != null ? String(item.consumption_quantity) : '',
    consumption_frequency: item?.consumption_frequency ?? '',
  });
  const [hazardous, setHazardous] = useState(Boolean(item?.hazardous));
  const [hazard, setHazard] = useState({
    hazard_composition: item?.hazard_composition ?? '',
    hazard_usage: item?.hazard_usage ?? '',
    hazard_dilution: item?.hazard_dilution ?? '',
    hazard_safety_measures: item?.hazard_safety_measures ?? '',
    hazard_supplier_info: item?.hazard_supplier_info ?? '',
  });

  const payload = (supplierId: string | null) => ({
    ...form,
    // `null` explícito, no `undefined`: así el servidor sabe que se quiso
    // vaciar el campo en vez de simplemente no tocarlo (§4).
    sku: form.sku || null,
    category: form.category || null,
    preferred_supplier_id: supplierId,
    consumption_quantity: form.consumption_quantity || null,
    consumption_frequency: form.consumption_frequency || null,
    hazardous,
    ...(hazardous
      ? {
        hazard_composition: hazard.hazard_composition || null,
        hazard_usage: hazard.hazard_usage || null,
        hazard_dilution: hazard.hazard_dilution || null,
        hazard_safety_measures: hazard.hazard_safety_measures || null,
        hazard_supplier_info: hazard.hazard_supplier_info || null,
      }
      : {
        hazard_composition: null, hazard_usage: null, hazard_dilution: null,
        hazard_safety_measures: null, hazard_supplier_info: null,
      }),
  });

  /**
   * El proveedor del alta rápida se crea **acá**, no cuando se tecleó el
   * nombre: hasta este momento existía solo en el estado de este modal, así
   * que cancelar no dejó nada en la base.
   *
   * Se crea antes del producto porque el producto necesita su id. Si el
   * producto fallara después, el proveedor sí queda creado — por eso lo
   * primero que hace el `then` es pasar la elección a id real: un segundo
   * intento reutiliza el que ya existe en vez de chocar contra
   * `unique (organization_id, name)`.
   */
  const mutation = useApiMutation(
    async () => {
      const resolved = await resolveSupplierId(supplier);
      if (resolved.created && resolved.id) setSupplier({ id: resolved.id, pendingName: null });

      const body = payload(resolved.id);
      await (item
        ? api.patch(`/admin/inventory/items/${item.id}`, body)
        : api.post('/admin/inventory/items', body));

      return { createdSupplierId: resolved.created ? resolved.id : null };
    },
    ['/admin/inventory', '/admin/suppliers'],
    {
      onSuccess: (result) => {
        onClose();
        if (result.createdSupplierId) onSupplierCreated(result.createdSupplierId);
      },
    },
  );
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setHazardField = (k: keyof typeof hazard) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setHazard((h) => ({ ...h, [k]: e.target.value }));

  return (
    <Modal
      title={item ? `Editar ${item.name}` : 'Nuevo producto'}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!form.name || mutation.isPending} onClick={() => mutation.mutate()}>
            {item ? 'Guardar' : 'Crear'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      {item ? (
        <Field label="Foto">
          <PhotoUploader
            uploadPath={`/admin/inventory/items/${item.id}/photo`}
            deletePath={`/admin/inventory/items/${item.id}/photo`}
            photoSrc={item.photo_key ? `/api/admin/inventory/items/${item.id}/photo?v=${item.photo_key}` : null}
            name={item.name}
            size={72}
            /* Un jabón no es una persona: máscara cuadrada al encuadrar y
               miniatura cuadrada en la previa, como en la lista de al lado. */
            forma="cuadrado"
            invalidate={['/admin/inventory']}
          />
        </Field>
      ) : (
        <Note tone="info">Guardá el producto primero y después podrás subirle una foto.</Note>
      )}

      <Field label="Nombre" required><input className="input" value={form.name} onChange={set('name')} autoFocus /></Field>
      <div className="grid grid--2">
        <Field label="Categoría"><input className="input" value={form.category} onChange={set('category')} /></Field>
        <Field label="SKU / código"><input className="input mono" value={form.sku} onChange={set('sku')} /></Field>
      </div>
      <Field
        label="Proveedor preferido"
        hint="Opcional. Es a quién se le compra normalmente; la compra concreta puede ser de otro."
      >
        <SupplierPicker
          value={supplier}
          onChange={setSupplier}
          suppliers={suppliers.data?.suppliers ?? []}
          loading={suppliers.isLoading}
          placeholder="Sin definir — buscá por nombre…"
        />
      </Field>

      <div className="grid grid--2">
        <Field label="Se compra por"><input className="input" value={form.purchase_unit} onChange={set('purchase_unit')} /></Field>
        <Field label="Unidades por compra" hint="Cuántas unidades de consumo trae una de compra.">
          <input className="input" type="number" min="1" value={form.purchase_unit_quantity}
            onChange={set('purchase_unit_quantity')} />
        </Field>
      </div>
      <div className="grid grid--2">
        <Field label="Se consume por"><input className="input" value={form.consumption_unit} onChange={set('consumption_unit')} /></Field>
        <Field label="Stock mínimo"><input className="input" type="number" min="0" value={form.minimum_stock} onChange={set('minimum_stock')} /></Field>
      </div>

      <Field
        label="Consumo esperado"
        hint="Informativo, para proyectar inventario más adelante. Opcional."
      >
        <div className="row">
          <input
            className="input" type="number" step="0.1" min="0" style={{ maxWidth: 120 }}
            placeholder="Cantidad" value={form.consumption_quantity} onChange={set('consumption_quantity')}
          />
          <span className="muted small">{form.consumption_unit} cada</span>
          <select
            className="select"
            value={form.consumption_frequency}
            onChange={(e) => setForm((f) => ({ ...f, consumption_frequency: e.target.value }))}
          >
            <option value="">Sin definir</option>
            <option value="DAILY">Día</option>
            <option value="WEEKLY">Semana</option>
            <option value="MONTHLY">Mes</option>
          </select>
        </div>
      </Field>

      <div className="radios mt-2">
        <label className={`radiocard${!hazardous ? ' is-on' : ''}`}>
          <input type="radio" checked={!hazardous} onChange={() => setHazardous(false)} />
          <span>
            <span className="radiocard__t">Producto no peligroso</span>
            <span className="radiocard__d">No necesita hoja de seguridad.</span>
          </span>
        </label>
        <label className={`radiocard${hazardous ? ' is-on' : ''}`}>
          <input type="radio" checked={hazardous} onChange={() => setHazardous(true)} />
          <span>
            <span className="radiocard__t">Producto peligroso</span>
            <span className="radiocard__d">Químico u otro producto que requiere hoja de seguridad.</span>
          </span>
        </label>
      </div>

      {hazardous && (
        <div className="mt-2">
          <Field label="Composición">
            <input className="input" value={hazard.hazard_composition} onChange={setHazardField('hazard_composition')} />
          </Field>
          <Field label="Uso">
            <input className="input" value={hazard.hazard_usage} onChange={setHazardField('hazard_usage')} />
          </Field>
          <Field label="Dilución recomendada">
            <input className="input" value={hazard.hazard_dilution} onChange={setHazardField('hazard_dilution')} />
          </Field>
          <Field label="Medidas de seguridad">
            <input className="input" value={hazard.hazard_safety_measures} onChange={setHazardField('hazard_safety_measures')} />
          </Field>
          <Field label="Proveedor / datos de la hoja de seguridad">
            <input className="input" value={hazard.hazard_supplier_info} onChange={setHazardField('hazard_supplier_info')} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

function ConsumptionModal({ item, onClose }: { item: Item; onClose: () => void }) {
  const [quantity, setQuantity] = useState('1');
  const units = useApi<{ units: { id: string; unit_code: string }[] }>('/admin/units');
  const [unitId, setUnitId] = useState('');

  const mutation = useApiMutation(
    () => api.post('/admin/inventory/consumption', {
      item_id: item.id, quantity, service_unit_id: unitId || undefined, moved_on: today(),
    }),
    ['/admin/inventory'],
    { onSuccess: onClose },
  );

  return (
    <Modal
      title={`Consumo · ${item.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
            Registrar salida
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      <Field label={`Cantidad en ${item.consumption_unit}`} required>
        <input className="input input--money" type="number" step="0.5" min="0.5" value={quantity}
          onChange={(e) => setQuantity(e.target.value)} />
      </Field>
      <Field label="Consultorio" hint="Asignarlo permite calcular la rentabilidad por consultorio.">
        <select className="select" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
          <option value="">Sin asignar</option>
          {(units.data?.units ?? []).map((u) => (
            <option key={u.id} value={u.id}>{u.unit_code}</option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

/**
 * Una compra. Cajas × unidades por caja = lo que entra al inventario — esa
 * cuenta la hace el servidor (`finance.ts POST /purchases`), acá solo se
 * arma la lista de líneas.
 */
function NewPurchaseModal({ onClose, onSupplierCreated }: {
  onClose: () => void;
  onSupplierCreated: (id: string) => void;
}) {
  const itemsQuery = useApi<{ items: Item[] }>('/admin/inventory');
  const suppliers = useSuppliers();
  const [supplier, setSupplier] = useState<SupplierChoice>(NO_SUPPLIER);
  const [purchasedOn, setPurchasedOn] = useState(today());
  const [documentNumber, setDocumentNumber] = useState('');
  const [lines, setLines] = useState<{ inventory_item_id: string; quantity: string; unit_price: string }[]>([]);

  const items = itemsQuery.data?.items ?? [];
  /* El proveedor entró a la validación: es obligatorio desde la migración
     0023 (decisión del dueño, P6.7) — con proveedor vacío el índice que evita
     cargar dos veces la misma factura no aplicaba. El servidor lo rechaza
     igual; esto solo evita mandar una petición que se sabe perdida. */
  const valid = isSupplierChosen(supplier)
    && lines.length > 0 && lines.every((l) => l.inventory_item_id && Number(l.quantity) > 0);

  // El proveedor del alta rápida se crea recién acá — cancelar el modal no
  // deja rastro. Mismo razonamiento que en `ItemModal`, comentado allá.
  const mutation = useApiMutation(
    async () => {
      const resolved = await resolveSupplierId(supplier);
      if (resolved.created && resolved.id) setSupplier({ id: resolved.id, pendingName: null });

      await api.post('/admin/purchases', {
        supplier_id: resolved.id,
        purchased_on: purchasedOn,
        document_number: documentNumber || undefined,
        items: lines.map((l) => ({
          inventory_item_id: l.inventory_item_id, quantity: l.quantity,
          // El campo se llena en colones; el servidor guarda céntimos (§21,
          // mismo criterio que el salario en Staff.tsx).
          unit_price: l.unit_price ? (parseMoneyInput(l.unit_price) ?? 0) : 0,
        })),
      });

      return { createdSupplierId: resolved.created ? resolved.id : null };
    },
    ['/admin/inventory', '/admin/transactions', '/admin/suppliers'],
    {
      onSuccess: (result) => {
        onClose();
        if (result.createdSupplierId) onSupplierCreated(result.createdSupplierId);
      },
    },
  );

  return (
    <Modal
      title="Nueva compra"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={!valid || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? 'Guardando…' : 'Registrar compra'}
          </Button>
        </>
      }
    >
      {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}

      <Field label="Proveedor" required hint="A quién se le compró. Sin proveedor no se puede registrar.">
        <SupplierPicker
          value={supplier}
          onChange={setSupplier}
          suppliers={suppliers.data?.suppliers ?? []}
          loading={suppliers.isLoading}
        />
      </Field>
      <Field label="Fecha" required>
        <input className="input" type="date" value={purchasedOn} onChange={(e) => setPurchasedOn(e.target.value)} />
      </Field>
      <Field
        label="N.º de factura"
        hint="Con el número, el sistema rechaza la misma factura cargada dos veces."
      >
        <input className="input" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} />
      </Field>

      <Field
        label="Productos"
        required
        hint="El precio es por unidad de compra (por caja, no por unidad de consumo), en colones: 5000 son ₡5.000."
      >
        {lines.map((line, i) => (
          // `row--wrap`: el select no encoge por debajo de su opción más larga,
          // así que sin envolver la fila se sale del modal en un teléfono.
          <div className="row row--wrap mb-1" key={i}>
            <select
              className="select"
              style={{ flex: '1 1 100%', minWidth: 0 }}
              value={line.inventory_item_id}
              onChange={(e) => setLines((prev) => prev.map((l, j) =>
                (j === i ? { ...l, inventory_item_id: e.target.value } : l)))}
            >
              <option value="">Elegí un producto…</option>
              {items.map((it) => <option key={it.id} value={it.id}>{it.name} ({it.purchase_unit})</option>)}
            </select>
            <input
              className="input" type="number" min="1" style={{ maxWidth: 90 }}
              placeholder="Cant."
              value={line.quantity}
              onChange={(e) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l)))}
            />
            <input
              className="input input--money" type="text" inputMode="numeric" style={{ maxWidth: 130 }}
              placeholder="₡ por caja"
              value={line.unit_price}
              onChange={(e) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, unit_price: e.target.value } : l)))}
            />
            <Button onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>Quitar</Button>
          </div>
        ))}
        <Button onClick={() => setLines((prev) => [...prev, { inventory_item_id: '', quantity: '1', unit_price: '' }])}>
          Agregar producto
        </Button>
      </Field>
    </Modal>
  );
}
